import { HttpParams } from '@angular/common/http';

export class FindNumberReq {
  keyword?: string;
  capabilities?: string[];
  productId?: string;
  sku?: string;
  appIds?: string[];
  subscriptionUuids?: string[];
  states?: string;

  constructor(obj?: Partial<FindNumberReq>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }

  toParams(): HttpParams {
    let params = new HttpParams();
    if (this.keyword) {
      params = params.set('keyword', this.keyword);
    }
    if (this.capabilities) {
      params = params.set('capabilities', this.capabilities.join(','));
    }
    if (this.productId) {
      params = params.set('productId', this.productId);
    }
    if (this.sku) {
      params = params.set('sku', this.sku);
    }
    if (this.appIds) {
      params = params.set('appIds', this.appIds.join(','));
    }
    if (this.subscriptionUuids) {
      params = params.set('subscriptionUuids', this.subscriptionUuids.join(','));
    }
    if (this.states) {
      params = params.set('states', this.states);
    }
    return params;
  }
}

export class B3Number {
  number: string;
  country: string;
  capability: string;
  productId: string;
  appId: string;
  expiryTime: string;
  subscriptionUuid: string;
  state: string;
  sku: string;
  ownerOrgUuid: string;
  licenseId?: string;

  constructor(obj?: Partial<B3Number>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }
}

export class CheckDocumentResult {
  sku: string;
  valid: boolean;
}

// Store
export class NumberCapability {
  voice: boolean;
  sms: boolean;
  fax: boolean;

  constructor(parameters?: any) {
    this.voice = parameters.voice;
    this.sms = parameters.sms;
    this.fax = parameters.fax;
  }

  getString(): string {
    const tmp = [];
    if (this.voice) {
      tmp.push('voice');
    }

    if (this.sms) {
      tmp.push('sms');
    }

    if (this.fax) {
      tmp.push('fax');
    }

    return tmp.join(',').toUpperCase();
  }
}

export class NumberResponse {
  number: string;
  pricingCode: string;
  state: string;
  capability: NumberCapability;
  expiryTime: string;
  subscriptionUuid: string;
  numberState: string;
}

export class NumberVendor {
  vendors: NumberVendorDetail[] = [];
  selected: NumberVendorDetail;
}

export class NumberVendorDetail {
  productId: string;
  name: string;
  skuDetailList: NumberSkuDetail[];
  selectedSku: NumberSkuDetail;
}

export class NumberSkuDetail {
  sku: string;
  name: string;
  price: number;
}

export class AssignNumberRequest {
  action = 'assign';

  constructor(
    public data: AssignNumberData,
    public remarks: string
  ) {}
}

export class AssignNumberData {
  constructor(
    public appUuid: string,
    public subscriptionUuid: string
  ) {}
}

export class ReserveNumberRequest {
  action = 'reserve';
  constructor(
    public data: ReserveNumberData,
    public remarks: string
  ) {}
}

export class ReserveNumberData {
  constructor(public duration: ReserveNumberDuration) {}
}

export class ReserveNumberDuration {
  // {Hours, Minutes, ... }
  constructor(
    public value: number,
    public type: string
  ) {}
}

export class MultiReserveResp {
  successList: any[];
  errorList: any[];
}

export enum NumberState {
  Available,
  Reserved
}

export interface NumberModel {
  number: string;
  vendorCode: string;
  state: string;
  sku: string;
  expiryTime: string;
  capabilities: string;
  routing: string;
  appId: string;
  subscriptionUuid: string;
  reservedSubscriptionUuid: string;
  reserveForPartner: boolean;
  ownerOrgUuid: string;
  isPortedIn: boolean;
  isGolden: boolean;
  isMsisdn: boolean;
  isDevice: boolean;
  providerUuid: string;
  note: string;
  document: string;
  supplierId: string;
}

export enum NumberType {
  Fixed = 'fixed',
  Mobile = 'mobile'
}

export enum DeviceType {
  Physical = 'physical',
  Virtual = 'virtual',
  ESIM = 'esim'
}

export class ByoiNumber {
  number: string;
  country: string;
  vendorCode: string;
  numberType: NumberType;
  deviceType: DeviceType;
  routing: string;
  ims: Ims;
  visibleToDomain: string;
  ownerDomain: string;
  reserveForPartner: boolean;
  reservedSubscriptionUuid: string;
  isPortedIn: boolean;
  isGolden: boolean;
  isMsisdn: boolean;
  isDevice: boolean;
  state: string;
  createdTime: Date;
  updatedTime: Date;
  note: string;
  chargeOTC: boolean;
  supplierId: string;
  providerUuid: string;
  sku: string;
  appId: string;
  productId: string;
  capabilities: string;
  ownerOrgUuid: string;
  simRouting: string;
  msteamRouting: string;
  byoaRouting: string;
  zoomRouting: string;
  subscriptionUuid: string;
  expiryTime: string;

  constructor(obj?: Partial<ByoiNumber>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }
}

export interface Ims {
  imsi: null;
  ownerDomain: null;
  remoteProxyDomain: null;
  ueServer: null;
  msisdn: null;
}

export class GroupNumberHistory {
  public orgUuid: string;
  public numberHistories: NumberHistory[] = [];

  constructor(orgUuid: string) {
    this.orgUuid = orgUuid;
  }

  public addNumHistory(numberHistory: NumberHistory) {
    this.numberHistories.unshift(numberHistory);
  }

  public hasHistoryData(): boolean {
    return this.numberHistories.length > 0;
  }
}

export class NumberHistory {
  private static RESERVED_ACTION_SUBSTR = 'reserved [{number}] for organization [';
  private static RELEASE_ACTION_SUBSTR = 'released [{number}] from organization [';
  private static CHANGE_OWNER_ACTION_SUBSTR = 'changed [{number}] owner from [';

  public number: string;
  public remark: string;
  public owner: string;
  public transferFromOrg: string;
  public action: string;

  constructor(num: string, remark: string) {
    this.number = num.indexOf('+') === 0 ? num : '+' + num;
    this.action = this.getAction(this.number, remark);
    this.owner = this.getOwner(this.number, remark);
    this.transferFromOrg = this.getTransferFromOrg(this.number, remark);
    this.remark = remark
      .replace(this.owner, this.shortUuid(this.owner))
      .replace(this.transferFromOrg, this.shortUuid(this.transferFromOrg));
  }

  public isRelease(): boolean {
    return this.action && this.action === 'RELEASE';
  }

  public isReserved(): boolean {
    return this.action && this.action === 'RESERVE';
  }

  public isChangedOwner(): boolean {
    return this.action && this.action === 'CHANGE_OWNER';
  }

  private getAction(num: string, remark: string) {
    if (remark.indexOf(NumberHistory.RESERVED_ACTION_SUBSTR.replace('{number}', num)) > -1) {
      return 'RESERVE';
    }

    if (remark.indexOf(NumberHistory.RELEASE_ACTION_SUBSTR.replace('{number}', num)) > -1) {
      return 'RELEASE';
    }

    if (remark.indexOf(NumberHistory.CHANGE_OWNER_ACTION_SUBSTR.replace('{number}', num)) > -1) {
      return 'CHANGE_OWNER';
    }

    return null;
  }

  private getOwner(num: string, remark: string) {
    if (this.isRelease()) {
      const str = NumberHistory.RELEASE_ACTION_SUBSTR.replace('{number}', num);
      const fromIdx = remark.indexOf(str) + str.length;
      return remark.substr(fromIdx, remark.indexOf(']', fromIdx) - fromIdx).trim();
    }

    if (this.isReserved()) {
      const str = NumberHistory.RESERVED_ACTION_SUBSTR.replace('{number}', num);
      const fromIdx = remark.indexOf(str) + str.length;
      return remark.substr(fromIdx, remark.indexOf(']', fromIdx) - fromIdx).trim();
    }

    if (this.isChangedOwner()) {
      const str = NumberHistory.CHANGE_OWNER_ACTION_SUBSTR.replace('{number}', num);
      const fromIdx = remark.indexOf('to [', remark.indexOf(str) + str.length) + 4;
      return remark.substr(fromIdx, remark.indexOf(']', fromIdx) - fromIdx).trim();
    }

    return '';
  }

  private getTransferFromOrg(num: string, remark: string): string {
    if (this.isChangedOwner()) {
      const str = NumberHistory.CHANGE_OWNER_ACTION_SUBSTR.replace('{number}', num);
      const fromIdx = remark.indexOf(str) + str.length;
      return remark.substr(fromIdx, remark.indexOf(']', fromIdx) - fromIdx).trim();
    }

    return '';
  }

  private shortUuid(uuid: string): string {
    return uuid ? uuid.substr(0, 8) : uuid;
  }
}

export class AddRequest {
  numberListKey: string;
  vendorCode: string;
  country: string;
  sku: string;
  capabilities: string;
  supplierId?: string;
  reserveForOrgUuid: string;
  numberType?: NumberType;
  deviceType?: DeviceType;
  visibleToDomain: string;
  isGolden?: boolean;
}

export interface ByoiVendors {
  code: string;
  name: string;
  type: null;
  status: string;
  note: null;
  supplierUuid: null;
  createdTime: Date;
  updatedTime: Date;
  isByoi: boolean;
  byoiDomains: string[];
  byoiRouting: string;
  byoiProductSkus: ByoiProductSkus;
}

export interface ByoiProductSkus {
  [key: string]: Country;
}

export interface Country {
  id: string;
  sku: string;
  activationFeeProductId: string;
  activationFeeSku: string;
}

export interface UpdateAttributesRequest {
  deviceType: DeviceType;
  numberType: NumberType;
  golden: boolean;
}
